import { useMemo } from 'react';

import { useAppSelector } from '../store/store';
import { PAYWALL_DISABLED } from '../constants/maestra';
import type { SubscriptionState } from '../store/slices/subscription';
import { assinaturaValeComoPro } from '../nucleo/perfilAtivo';

// ─── Types ────────────────────────────────────────────────────────────────────
// Eixo CONTA (assinatura Maestra PRO, mensal ou anual), agora com os add-ons de PERFIL e de
// ARMAZENAMENTO que a mesma assinatura carrega. O estado por perfil (legado vitalício ou dentro
// da cota) e o papel do usuário (dono/colaborador) ficam em useArtistCapabilities.

export type Plan = 'free' | 'pro';

export interface Entitlements {
  plan: Plan;
  isPro: boolean; // assinatura PRO ativa (ou em carência)
  maxCatalogTracks: number; // 10 (sem PRO) | Infinity (PRO) — por perfil, mas o gate é a conta
  /** Perfis que a assinatura cobre (incluso + add-on). Os legados NÃO entram nesta conta. */
  profileSlots: number;
  /** Cota e uso de armazenamento, em bytes. */
  storageQuotaBytes: number;
  storageUsedBytes: number;
}

/**
 * Cotas da conta, como o SERVIDOR as reporta (`asaas-subscription-status`).
 *
 * A regra de quem tem direito a quanto espaço vive em SQL (`public.storage_quota_bytes`), porque
 * é ela que também barra o envio no gatilho do Storage. Reimplementá-la aqui criaria duas
 * verdades que divergem no dia em que alguém mexer só numa — e a que vale, para o usuário, é a
 * que recusa o arquivo. Aqui só transportamos o número.
 */
export interface CotasDaConta {
  includedProfiles: number;
  extraProfiles: number;
  storageQuotaBytes: number;
  storageUsedBytes: number;
}

// ─── Pure Derivation Function ─────────────────────────────────────────────────

export const FREE_MAX_CATALOG_TRACKS = 10;

const SEM_COTA: CotasDaConta = {
  includedProfiles: 0,
  extraProfiles: 0,
  storageQuotaBytes: 0,
  storageUsedBytes: 0,
};

export function deriveEntitlements(
  status: SubscriptionState['status'],
  gracePeriodEndsAt: string | null,
  now: number = Date.now(),
  cotas: CotasDaConta = SEM_COTA
): Entitlements {
  if (PAYWALL_DISABLED) {
    return {
      plan: 'pro',
      isPro: true,
      maxCatalogTracks: Infinity,
      profileSlots: Infinity,
      storageQuotaBytes: Infinity,
      storageUsedBytes: cotas.storageUsedBytes,
    };
  }

  const isPro = assinaturaValeComoPro(status, gracePeriodEndsAt, now);

  return {
    plan: isPro ? 'pro' : 'free',
    isPro,
    maxCatalogTracks: isPro ? Infinity : FREE_MAX_CATALOG_TRACKS,
    // Sem assinatura não há slot nenhum: um perfil legado continua acessível pelo caminho do
    // vitalício (useArtistCapabilities), e não por ocupar cota.
    profileSlots: isPro ? cotas.includedProfiles + cotas.extraProfiles : 0,
    storageQuotaBytes: cotas.storageQuotaBytes,
    storageUsedBytes: cotas.storageUsedBytes,
  };
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useEntitlements(): Entitlements {
  const status = useAppSelector((state) => state.subscription.status);
  const gracePeriodEndsAt = useAppSelector((state) => state.subscription.gracePeriodEndsAt);
  const includedProfiles = useAppSelector((state) => state.subscription.includedProfiles);
  const extraProfiles = useAppSelector((state) => state.subscription.extraProfiles);
  const storageQuotaBytes = useAppSelector((state) => state.subscription.storageQuotaBytes);
  const storageUsedBytes = useAppSelector((state) => state.subscription.storageUsedBytes);

  return useMemo(
    () =>
      deriveEntitlements(status, gracePeriodEndsAt, Date.now(), {
        includedProfiles,
        extraProfiles,
        storageQuotaBytes,
        storageUsedBytes,
      }),
    [status, gracePeriodEndsAt, includedProfiles, extraProfiles, storageQuotaBytes, storageUsedBytes]
  );
}
